import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {whenRouterHasNavigatedTo, withLatestFromSelectors} from '@store/common/effects.helpers';
import {catchError, exhaustMap, map, mergeMap, of, switchMap} from 'rxjs';
import {RouterSelectors, SnackBarActions} from '@store/aviary/aviary.adapter';
import {ItemRequestsActions} from './item-requests.actions';
import {ItemRequestsService} from '@store/item-requests/item-requests.service';
import {openConfirmationDialog, openFormDialog} from '@aviary/store/common/effect-dialog.helpers';
import {CreateItemRequestValue} from '@store/item-requests/item-requests.types';
import {CreateItemRequestDialog} from '@app/my-account/item-requests/item-request-actions/create-item-request.dialog';
import {filter} from 'rxjs/operators';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {ItemRequestsSelectors} from '@store/item-requests/item-requests.selectors';
import {CancelItemRequestDialog} from '@app/my-account/item-requests/item-request-actions/cancel-item-request.dialog';
import {PatronActions} from '@store/patron/patron.actions';
import {HoldsActions} from '@store/holds/holds.actions';

@Injectable({providedIn: 'root'})
export class ItemRequestsEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly service: ItemRequestsService = inject(ItemRequestsService);

  public loadItemRequestsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('ITEM_REQUESTS'),
      mergeMap(() => [
        ItemRequestsActions.loadActiveItemRequests(),
        ItemRequestsActions.loadClosedItemRequests()
      ])
    )
  );

  public loadActiveItemRequests = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.loadActiveItemRequests, ItemRequestsActions.createItemRequestSuccess, ItemRequestsActions.cancelItemRequestSuccess),
      switchMap(() => this.service.loadItemRequests({pageSize: 200}, ['REQUESTED', 'PENDING']).pipe(
        map(response => ItemRequestsActions.loadActiveItemRequestsSuccess(response)),
        catchError((error: unknown) => of(ItemRequestsActions.loadActiveItemRequestsFailure(error)))
      ))
    )
  );

  public loadClosedItemRequests = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.loadClosedItemRequests, ItemRequestsActions.cancelItemRequestSuccess),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([, queryParams]) => this.service.loadItemRequests(queryParams, ['APPROVED', 'DECLINED', 'CANCELED']).pipe(
        map(response => ItemRequestsActions.loadClosedItemRequestsSuccess(response)),
        catchError((error: unknown) => of(ItemRequestsActions.loadClosedItemRequestsFailure(error)))
      ))
    )
  );

  public loadHoldDataOnLoadClosedItemRequestsSuccess = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.loadClosedItemRequestsSuccess),
      withLatestFromSelectors(ItemRequestsSelectors.ClosedItemRequests.linkedCatalogRecordIds),
      map(([, ids]) => HoldsActions.loadHoldsForCatalogRecords(ids))
    )
  );

  public showCreateItemRequest = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.showCreateItemRequest),
      openFormDialog(() => ({
        componentClass: CreateItemRequestDialog,
        data: undefined,
        onConfirm: (value: CreateItemRequestValue) => ItemRequestsActions.createItemRequest(value)
      }))
    )
  );

  public createItemRequest = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.createItemRequest),
      exhaustMap(({value}) => this.service.createItemRequest(value).pipe(
        mergeMap(() => [
          SnackBarActions.showSuccess('Request submitted successfully.'),
          ItemRequestsActions.createItemRequestSuccess()
        ]),
        catchError((error: unknown) => of(
          ItemRequestsActions.createItemRequestFailure(error))
        )
      ))
    )
  );

  public refreshAccountStatusOnCreateItemRequestSuccess = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.createItemRequestSuccess),
      map(() => PatronActions.loadAccountStatus())
    )
  );

  public loadItemRequestOnRouterNavigatedToItemRequestDetails = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('ITEM_REQUEST_DETAILS'),
      withLatestFromSelectors(RouterSelectors.paramValueAsNumberWithFallback('id', 0), ItemRequestsSelectors.Current.id),
      filter(([, idParam, currentId]) => idParam !== currentId),
      map(([, idParam]) => ItemRequestsActions.loadItemRequestDetails(Number(idParam))),
    )
  );

  public loadItemRequestDetails = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.loadItemRequestDetails),
      switchMap(({id}) => this.service.loadItemRequestDetails(id).pipe(
        map(response => ItemRequestsActions.loadItemRequestDetailsSuccess(response)),
        catchError((error: unknown) => of(ItemRequestsActions.loadItemRequestDetailsFailure(error)))
      ))
    )
  );

  public sendItemRequestMessage = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.sendItemRequestMessage),
      switchMap(({itemRequestId, message}) => this.service.sendItemRequestMessage(itemRequestId, message).pipe(
        map(() => ItemRequestsActions.loadItemRequestDetails(itemRequestId)),
        catchError((error: unknown) => of(
          ItemRequestsActions.sendItemRequestMessageFailure(error))
        )
      ))
    )
  );

  public markItemRequestMessagesAsRead = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.markItemRequestMessagesAsRead),
      withLatestFromSelectors(RouterSelectors.paramValue('id')),
      filter(([, idParam]) => isDefined(idParam)),
      switchMap(([, idParam]) => this.service.markItemRequestMessagesAsRead(Number(idParam)).pipe(
        map(() => ItemRequestsActions.markItemRequestMessagesAsReadSuccess()),
        catchError((error: unknown) => of(
          ItemRequestsActions.markItemRequestMessagesAsReadFailure(error))
        )
      ))
    )
  );

  public showCancelItemRequest = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.showCancelItemRequest),
      openConfirmationDialog(({itemRequest}) => ({
        componentClass: CancelItemRequestDialog,
        data: {itemRequest},
        onConfirm: () => ItemRequestsActions.cancelItemRequest(itemRequest)
      }))
    )
  );

  public cancelItemRequest = createEffect(() =>
    this.actions.pipe(
      ofType(ItemRequestsActions.cancelItemRequest),
      exhaustMap(({itemRequest}) => this.service.cancelItemRequest(itemRequest.id).pipe(
        mergeMap(() => [
          SnackBarActions.showSuccess('Request successfully canceled.'),
          ItemRequestsActions.cancelItemRequestSuccess()
        ]),
        catchError((error: unknown) => of(
          ItemRequestsActions.cancelItemRequestFailure(error))
        )
      ))
    )
  );
}
